/**
 * Cabinet → Limbo / Review & Confirm (Phase A)
 *
 * Rendered when the signed-in user's `customer_status` is not yet 'active'.
 * Three states:
 *   - pending_admin_review      → friendly waiting screen
 *   - pending_customer_confirmation → Review & Confirm (read-only summary + Confirm + Request changes)
 *   - revisions_requested       → waiting state after customer asked for changes
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle2, Clock, AlertCircle, Loader2, MessageSquare, LogOut, ArrowLeft, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import BrandMark from '@/cabinet/components/layout/BrandMark';
import LanguageToggle from '@/cabinet/components/layout/LanguageToggle';
import { useI18n } from '@/cabinet/i18n';
import { useAuth } from '@/cabinet/context/AuthContext';
import { api, formatApiError } from '@/cabinet/lib/api';

const STATUS_META = {
  pending_admin_review: {
    color: 'bg-amber-100 text-amber-900',
    icon: Clock,
  },
  pending_customer_confirmation: {
    color: 'bg-blue-100 text-blue-900',
    icon: CheckCircle2,
  },
  revisions_requested: {
    color: 'bg-rose-100 text-rose-900',
    icon: AlertCircle,
  },
  rejected: {
    color: 'bg-neutral-200 text-neutral-800',
    icon: XCircle,
  },
};

export default function LimboPage() {
  const { t, lang } = useI18n();
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const status = user?.customer_status || 'pending_admin_review';
  const meta = STATUS_META[status] || STATUS_META.pending_admin_review;
  const Icon = meta.icon;

  const [pets, setPets] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [subs, setSubs] = useState([]);
  const [products, setProducts] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectNote, setRejectNote] = useState('');
  const [rejecting, setRejecting] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [p, a, s, pr] = await Promise.all([
          api.get('/pets'),
          api.get('/addresses'),
          api.get('/subscriptions'),
          api.get('/cabinet/products'),
        ]);
        if (!alive) return;
        setPets(p.data || []);
        setAddresses(a.data || []);
        setSubs(s.data || []);
        setProducts(pr.data || []);
      } catch (_e) {
        // Non-fatal — limbo page still renders.
      }
    })();
    return () => { alive = false; };
  }, []);

  const onConfirm = async () => {
    setConfirming(true);
    try {
      await api.post('/users/me/confirm-profile');
      toast.success(t('limbo.confirm_success'));
      await refresh();
      navigate('/cabinet/dashboard', { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setConfirming(false);
    }
  };

  const onRequestChanges = async () => {
    const trimmed = message.trim();
    if (!trimmed) {
      toast.error(t('limbo.request_message_required'));
      return;
    }
    setRequesting(true);
    try {
      await api.post('/users/me/request-revisions', { message: trimmed });
      toast.success(t('limbo.request_sent'));
      setDialogOpen(false);
      setMessage('');
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRequesting(false);
    }
  };

  const onReject = async () => {
    if (!rejectReason) {
      toast.error(t('limbo.reject_reason_required'));
      return;
    }
    setRejecting(true);
    try {
      await api.post('/users/me/reject-offer', {
        reason: rejectReason,
        note: rejectNote.trim(),
      });
      toast.success(t('limbo.reject_sent'));
      setRejectOpen(false);
      setRejectReason('');
      setRejectNote('');
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRejecting(false);
    }
  };

  const onLogout = async () => {
    await logout();
    navigate('/cabinet/login', { replace: true });
  };

  const productById = Object.fromEntries((products || []).map((p) => [p.product_id, p]));

  return (
    <div className="min-h-screen bg-cream" data-testid="limbo-page">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-[hsl(var(--border))] bg-cream/90 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-[1100px] items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <BrandMark />
            <span className="hidden sm:inline text-xs text-[hsl(var(--muted-foreground))]">| {t('brand.tagline')}</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] bg-paper px-3 py-1.5 text-xs font-bold hover:border-[hsl(var(--terracotta))] hover:text-[hsl(var(--terracotta))] transition-colors" data-testid="limbo-back-to-site">
              <ArrowLeft className="h-3.5 w-3.5" />
              {lang === 'ka' ? 'მთავარ გვერდზე' : 'Main site'}
            </Link>
            <LanguageToggle />
            <Button onClick={onLogout} variant="ghost" size="sm" className="text-[hsl(var(--muted-foreground))]" data-testid="limbo-logout">
              <LogOut className="h-4 w-4 mr-1.5" /> {t('nav.logout')}
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[760px] px-4 py-10 sm:py-14">
        {/* Status badge */}
        <div className="flex items-start gap-4">
          <div className={`rounded-2xl p-3 ${meta.color}`}>
            <Icon className="h-8 w-8" />
          </div>
          <div className="flex-1">
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[hsl(var(--terracotta))]">
              {t(`limbo.${status}.eyebrow`)}
            </p>
            <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-[hsl(var(--foreground))] mt-1">
              {t(`limbo.${status}.title`, { name: (user?.name || '').split(' ')[0] || '' })}
            </h1>
            <p className="text-[hsl(var(--muted-foreground))] mt-2 text-base leading-relaxed">
              {t(`limbo.${status}.subtitle`)}
            </p>
          </div>
        </div>

        {/* Admin notes (visible only when admin finalized) */}
        {status === 'pending_customer_confirmation' && user?.admin_notes && (
          <Card className="mt-6 border-[hsl(var(--terracotta))]/30 bg-[hsl(var(--terracotta))]/8" data-testid="limbo-admin-notes">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2 text-[hsl(var(--terracotta))] font-bold">
                <MessageSquare size={16} />
                {t('limbo.admin_notes_title')}
              </div>
              <p className="text-sm text-[hsl(var(--foreground))] whitespace-pre-line leading-relaxed">{user.admin_notes}</p>
            </CardContent>
          </Card>
        )}

        {/* Profile summary */}
        <Card className="mt-6 border-[hsl(var(--border))] bg-paper" data-testid="limbo-profile-summary">
          <CardContent className="p-5 space-y-4">
            <h2 className="font-serif text-lg font-semibold text-[hsl(var(--foreground))]">{t('limbo.profile_summary_title')}</h2>

            <Row label={t('limbo.field_name')} value={user?.name} />
            <Row label={t('limbo.field_email')} value={user?.email} />
            <Row label={t('limbo.field_phone')} value={user?.phone || '—'} />

            {pets.length > 0 && (
              <Section title={t('limbo.section_pets', { count: pets.length })}>
                {pets.map((p) => (
                  <div key={p.pet_id} className="text-sm">
                    <strong className="text-[hsl(var(--foreground))]">{p.name}</strong>
                    <span className="text-[hsl(var(--muted-foreground))]"> · {p.species}{p.breed ? ` · ${p.breed}` : ''}{p.weight_kg ? ` · ${p.weight_kg}kg` : ''}</span>
                  </div>
                ))}
              </Section>
            )}

            {addresses.length > 0 && (
              <Section title={t('limbo.section_addresses', { count: addresses.length })}>
                {addresses.map((a) => (
                  <div key={a.address_id} className="text-sm">
                    <strong className="text-[hsl(var(--foreground))]">{a.label}</strong>{a.is_default && <Badge variant="secondary" className="ml-2 rounded-full text-[10px]">{t('limbo.default_badge')}</Badge>}
                    <div className="text-[hsl(var(--muted-foreground))] text-xs">{[a.street, a.building, a.apartment, a.district, a.city].filter(Boolean).join(', ')}</div>
                  </div>
                ))}
              </Section>
            )}

            {subs.length > 0 && (
              <Section title={t('limbo.section_subs', { count: subs.length })}>
                {subs.map((s) => (
                  <div key={s.subscription_id} className="text-sm">
                    <strong className="text-[hsl(var(--foreground))]">{s.label || `${t('limbo.section_subs_item')} #${s.subscription_id.slice(-4)}`}</strong>
                    <span className="text-[hsl(var(--muted-foreground))]"> · {s.frequency} · {(s.items || []).length} {t('limbo.items_short')}</span>
                    {(s.items || []).length > 0 && (
                      <ul className="mt-1 ml-3 text-xs text-[hsl(var(--muted-foreground))] list-disc list-inside">
                        {(s.items || []).slice(0, 6).map((it, i) => {
                          const p = productById[it.product_id];
                          return <li key={i}>{p ? (lang === 'ka' ? p.name_ka : p.name_en) : (it.product_name || it.product_id)} × {it.qty}</li>;
                        })}
                      </ul>
                    )}
                  </div>
                ))}
              </Section>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        {status === 'pending_customer_confirmation' && (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="limbo-actions">
            <Button
              onClick={onConfirm}
              disabled={confirming}
              className="h-12 rounded-xl bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/90 text-white font-bold"
              data-testid="limbo-confirm-btn"
            >
              {confirming ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              {t('limbo.confirm_button')}
            </Button>
            <Button
              onClick={() => setDialogOpen(true)}
              variant="outline"
              className="h-12 rounded-xl"
              data-testid="limbo-request-changes-btn"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              {t('limbo.request_changes_button')}
            </Button>
            <Button
              onClick={() => setRejectOpen(true)}
              variant="outline"
              className="h-12 rounded-xl border-rose-300 text-rose-800 hover:bg-rose-50 hover:text-rose-900"
              data-testid="limbo-reject-btn"
            >
              <XCircle className="h-4 w-4 mr-2" />
              {t('limbo.reject_button')}
            </Button>
          </div>
        )}

        <p className="mt-6 text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">
          {t('limbo.footer_note')}
        </p>
      </main>

      {/* Request changes dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="limbo-request-dialog" className="bg-cream">
          <DialogHeader>
            <DialogTitle>{t('limbo.request_dialog_title')}</DialogTitle>
            <DialogDescription>{t('limbo.request_dialog_subtitle')}</DialogDescription>
          </DialogHeader>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t('limbo.request_dialog_placeholder')}
            rows={5}
            data-testid="limbo-request-message"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="limbo-request-cancel">
              {t('limbo.request_dialog_cancel')}
            </Button>
            <Button onClick={onRequestChanges} disabled={requesting} className="bg-[hsl(var(--terracotta))]" data-testid="limbo-request-submit">
              {requesting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {t('limbo.request_dialog_submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject offer dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="limbo-reject-dialog" className="bg-cream">
          <DialogHeader>
            <DialogTitle>{t('limbo.reject_dialog_title')}</DialogTitle>
            <DialogDescription>{t('limbo.reject_dialog_subtitle')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold">
                {t('limbo.reject_reason_label')}
              </label>
              <Select value={rejectReason} onValueChange={setRejectReason}>
                <SelectTrigger className="h-11 mt-1" data-testid="limbo-reject-reason">
                  <SelectValue placeholder={t('limbo.reject_reason_placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="price_too_high">{t('limbo.reject_reason_price_too_high')}</SelectItem>
                  <SelectItem value="products_not_suitable">{t('limbo.reject_reason_products_not_suitable')}</SelectItem>
                  <SelectItem value="delivery_schedule_mismatch">{t('limbo.reject_reason_delivery_schedule_mismatch')}</SelectItem>
                  <SelectItem value="found_alternative">{t('limbo.reject_reason_found_alternative')}</SelectItem>
                  <SelectItem value="not_ready_yet">{t('limbo.reject_reason_not_ready_yet')}</SelectItem>
                  <SelectItem value="other">{t('limbo.reject_reason_other')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold">
                {t('limbo.reject_note_label')}
              </label>
              <Textarea
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                placeholder={t('limbo.reject_note_placeholder')}
                rows={3}
                data-testid="limbo-reject-note"
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)} data-testid="limbo-reject-cancel">
              {t('limbo.reject_dialog_cancel')}
            </Button>
            <Button onClick={onReject} disabled={rejecting || !rejectReason} className="bg-rose-700 hover:bg-rose-800 text-white" data-testid="limbo-reject-submit">
              {rejecting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <XCircle className="h-4 w-4 mr-2" />}
              {t('limbo.reject_dialog_submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-[hsl(var(--border))]/40 pb-1.5 last:border-0 last:pb-0">
      <span className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{label}</span>
      <span className="text-sm text-[hsl(var(--foreground))] font-medium text-right">{value || '—'}</span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border-t border-[hsl(var(--border))]/40 pt-3">
      <h3 className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))] font-bold mb-1.5">{title}</h3>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}
